<script>
import { mapActions, mapGetters } from 'vuex'

import BodyContentInputPhone from './_BodyContentInputPhone.vue'
import BodyContentInputOtp from './_BodyContentInputOtp.vue'

const VERIFY_PHONE_NUMBER_STEP = {
  input_phone_number: 'input_phone_number',
  verify_code: 'verification_step',
}

const DEFAULT_CHANNEL_RECEIVE_OTP = 'sms'

export default {
  name: 'VerifyPhoneNumber',
  components: { BodyContentInputPhone, BodyContentInputOtp },
  data() {
    return {
      otp: '',
      submitting: false,
      errorMessage: '',
      step: VERIFY_PHONE_NUMBER_STEP.input_phone_number,
      submittedPhoneNumber: '',
      submittedCountryCallingCode: '',
      channelReceiveOtp: DEFAULT_CHANNEL_RECEIVE_OTP,
    }
  },
  computed: {
    ...mapGetters('userProfile', ['collectPhoneNumberDialogVisible']),
    dialogVisible: {
      get() {
        return (
          this.collectPhoneNumberDialogVisible &&
          this.$route.path !== '/login' &&
          this.$route.path !== '/chatbot' &&
          this.$route.path !== '/'
        )
      },
      set(value) {
        this.closeCollectPhoneNumberDialog(value)
      },
    },
    isInputPhoneNumberStep() {
      return this.step === VERIFY_PHONE_NUMBER_STEP.input_phone_number
    },
    isVerifyCodeStep() {
      return this.step === VERIFY_PHONE_NUMBER_STEP.verify_code
    },
  },
  watch: {
    collectPhoneNumberDialogVisible(newVal) {
      if (newVal) {
        this.resetState()
      }
    },
  },
  methods: {
    ...mapActions('userProfile', [
      'resendOtpCollectPhone',
      'verifyCollectPhoneByOtp',
      'addMerchantUserPhoneNumber',
      'closeCollectPhoneNumberDialog',
    ]),
    async handleSubmitAddPhoneNumber({ phoneNumber, countryCallingCode }) {
      this.submitting = true

      this.submittedPhoneNumber = phoneNumber

      if (this.submittedPhoneNumber.startsWith(countryCallingCode)) {
        this.submittedPhoneNumber = this.submittedPhoneNumber.slice(
          countryCallingCode.length
        )
      }

      if (this.submittedPhoneNumber.startsWith('0')) {
        this.submittedPhoneNumber = this.submittedPhoneNumber.slice(1)
      }

      this.submittedCountryCallingCode = countryCallingCode

      try {
        const response = await this.addMerchantUserPhoneNumber({
          phoneNumber: this.submittedPhoneNumber,
          countryCallingCode,
          channel: this.channelReceiveOtp,
        })

        this.handleAddPhoneNumberResponse(response)
      } catch (error) {
        const errorKey = error?.response?.data?.message || ''

        this.errorMessage = this.$t(errorKey)
      } finally {
        this.submitting = false
      }
    },
    handleAddPhoneNumberResponse(response) {
      this.step = response?.data?.step

      this.clearOtp()
      this.clearMessage()

      if (response?.data?.code) {
        this.otp = response.data.code
      }
    },
    async handleSubmitVerifyCode({ otp }) {
      this.submitting = true

      try {
        const response = await this.verifyCollectPhoneByOtp({
          otp,
          countryCallingCode: this.submittedCountryCallingCode,
          phoneNumber: this.submittedPhoneNumber,
        })

        this.handleVerifyCodeResponse(response)

        this.dialogVisible = false
      } catch (error) {
        const errorKey = error?.response?.data?.message || ''

        this.errorMessage = this.$t(errorKey)
      } finally {
        this.submitting = false
      }
    },
    async handleSubmitResend() {
      this.submittingResend = true

      try {
        const response = await this.resendOtpCollectPhone({
          channel: this.channelReceiveOtp,
          phoneNumber: this.submittedPhoneNumber,
          countryCallingCode: this.submittedCountryCallingCode,
        })

        this.handleResendOtpResponse(response)
      } catch (error) {
        const errorKey = error?.response?.data?.message || ''

        this.errorMessage = this.$t(errorKey)
      } finally {
        this.submittingResend = false
      }
    },
    handleChangePhoneNumber() {
      this.resetState()
    },
    clearMessage() {
      this.errorMessage = ''
    },
    clearOtp() {
      this.otp = ''
    },
    resetState() {
      this.errorMessage = ''
      this.step = VERIFY_PHONE_NUMBER_STEP.input_phone_number
      this.submittedPhoneNumber = ''
      this.submittedCountryCallingCode = ''
    },
    handleResendOtpResponse() {
      this.clearOtp()
      this.clearMessage()
    },
    handleVerifyCodeResponse() {
      this.closeCollectPhoneNumberDialog(false)
    },
  },
}
</script>

<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :show-close="!submitting"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    class="verify-phone-number__dialog is-lavender-design"
    width="100%"
  >
    <BodyContentInputPhone
      v-if="isInputPhoneNumberStep"
      :submitting="submitting"
      :error-message="errorMessage"
      @submit="handleSubmitAddPhoneNumber"
      @close="dialogVisible = false"
    />
    <BodyContentInputOtp
      v-else-if="isVerifyCodeStep"
      :otp.sync="otp"
      :phone-number="submittedPhoneNumber"
      :country-calling-code="submittedCountryCallingCode"
      :submitting="submitting"
      :error-message="errorMessage"
      @submit="handleSubmitVerifyCode"
      @resend="handleSubmitResend"
      @change:phone="handleChangePhoneNumber"
    />
  </el-dialog>
</template>

<style lang="scss" scoped>
@import '@/styles/_breakpoints';

.verify-phone-number__dialog {
  --dialog-body-padding: 24px;
  --verify-phone-number-dialog-width: 100vw;
  --border-radius: var(--tamara-border-radius-lg) var(--tamara-border-radius-lg)
    0 0;

  ::v-deep {
    .el-dialog {
      position: fixed;
      bottom: 0;
      max-width: var(--verify-phone-number-dialog-width);
      max-height: initial;
      border-radius: var(--border-radius);

      &__header {
        padding: 0;
      }

      &__headerbtn {
        display: none;
      }

      &__body {
        padding: var(--dialog-body-padding);
      }
    }
  }

  @include breakpoints(mobile) {
    --dialog-body-padding: 48px;
    --verify-phone-number-dialog-width: 525px;
    --border-radius: var(--tamara-border-radius-lg);

    ::v-deep {
      .el-dialog {
        position: relative;

        &__headerbtn {
          display: block;
          width: 40px;
          height: 40px;
          border: 1px solid #e5e5e5;
          border-radius: 100%;
          transition: all 300ms ease-out;

          .el-icon {
            color: var(--tamara-color-functional-grey-dark-3);
          }

          &:hover {
            border-color: var(--tamara-color-primary);
            transition: all 300ms ease-in;

            .el-icon {
              color: var(--tamara-color-primary);
            }
          }
        }
      }
    }
  }
}
</style>
